import { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, IconButton, InputAdornment,
  TextField, Tooltip, Typography,
} from '@mui/material'
import ChevronRightIcon  from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon   from '@mui/icons-material/ChevronLeft'
import SearchIcon        from '@mui/icons-material/Search'
import SelectAllIcon     from '@mui/icons-material/SelectAll'
import DeselectIcon      from '@mui/icons-material/Deselect'
import FilterAltOffIcon  from '@mui/icons-material/FilterAltOff'
import { setSelectedEntities } from '../../reducers/filtersReducer'
import { getSeriesColor, TECH_BADGE } from './LockdownKPIDashboardChartConfig'

export default function LockdownKPIDashboardLegend({ open = false, onToggle }) {
  const dispatch          = useDispatch()
  const charts            = useSelector(s => s.kpiData.charts)
  const aggLevel          = useSelector(s => s.filters.aggLevel)
  const selectedEntitiesMap = useSelector(s => s.filters.selectedEntities)
  const allSelected = Object.values(selectedEntitiesMap).flat()
  const [search, setSearch] = useState('')

  const tech1     = useSelector(s => s.filters.tech1)
  const tech2     = useSelector(s => s.filters.tech2)
  const kpiSel    = useSelector(s => s.filters.kpiSel)
  const numCharts = useSelector(s => s.filters.numCharts ?? 3)
  const comparing = tech2 !== null

  const entitiesByTech = useMemo(() => {
    const grouped = {}
    const visibleKeys = new Set(
      [tech1, tech2].filter(Boolean).flatMap(t =>
        (kpiSel[t] ?? []).slice(0, numCharts).map(kpi => `${t}::${kpi}`)
      )
    )
    charts
      .filter(c => {
        const t   = c.tech    ?? c.Tech    ?? ''
        const kpi = c.kpiName ?? c.KpiName ?? ''
        return visibleKeys.has(`${t}::${kpi}`)
      })
      .forEach(c => {
        const tech       = c.tech ?? c.Tech ?? ''
        const seriesList = c.series ?? c.Series ?? []
        if (!grouped[tech]) grouped[tech] = new Map()
        seriesList.forEach((s, i) => {
          const id = (s.entityId ?? s.EntityId ?? '').trim()
          if (id && !grouped[tech].has(id)) grouped[tech].set(id, i)
        })
      })
    return Object.fromEntries(
      Object.entries(grouped).map(([t, map]) => [t, Array.from(map.entries())])
    )
  }, [charts, tech1, tech2, kpiSel, numCharts])

  const allEntities = useMemo(() =>
    Object.values(entitiesByTech).flat()
  , [entitiesByTech])

  const filtered = useMemo(() =>
    search.trim()
      ? allEntities.filter(([id]) => id.toLowerCase().includes(search.toLowerCase()))
      : allEntities
  , [allEntities, search])

  const hasSelection = allSelected.length > 0

  const handleClick = (tech, id) => {
    const current = [...(selectedEntitiesMap[tech] ?? [])]
    const idx     = current.indexOf(id)
    if (idx >= 0) current.splice(idx, 1)
    else          current.push(id)
    dispatch(setSelectedEntities({ tech, entities: current }))
  }

  const handleSelectAll = () => {
    Object.entries(entitiesByTech).forEach(([tech, entities]) => {
      const filteredIds = search.trim()
        ? entities.filter(([id]) => id.toLowerCase().includes(search.toLowerCase())).map(([id]) => id)
        : entities.map(([id]) => id)
      const current = selectedEntitiesMap[tech] ?? []
      const allSel  = filteredIds.every(id => current.includes(id))
      const next    = allSel
        ? current.filter(id => !filteredIds.includes(id))
        : [...new Set([...current, ...filteredIds])]
      dispatch(setSelectedEntities({ tech, entities: next }))
    })
  }

  const handleClearAll = () => dispatch(setSelectedEntities({}))

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      width: open ? 200 : 28, minWidth: open ? 200 : 28,
      flexShrink: 0,
      borderLeft: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      transition: 'width .2s, min-width .2s',
      overflow: 'hidden',
      height: '100%',
      maxHeight: '100%',
    }}>

      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        justifyContent: open ? 'space-between' : 'center',
        px: open ? 1 : 0, py: '5px',
        borderBottom: '1px solid', borderColor: 'divider',
        flexShrink: 0, gap: 0.5,
      }}>
        {open && (
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', flex: 1 }}>
            {comparing
              ? Object.entries(entitiesByTech).map(([t, entities]) => (
                  <span key={t} style={{ marginRight: 6 }}>
                    <span style={{ fontWeight: 700 }}>{t}</span> ({entities.length})
                  </span>
                ))
              : <>{aggLevel} ({allEntities.length})</>
            }
          </Typography>
        )}

        {open && allSelected.length > 0 && (
          <Tooltip title="Clear selection">
            <IconButton size="small" onClick={handleClearAll} sx={{ p: '2px' }}>
              <FilterAltOffIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            </IconButton>
          </Tooltip>
        )}

        {open && (
          <Tooltip title={allEntities.length > 0 && allEntities.every(([id]) => allSelected.includes(id)) ? 'Deselect visible' : 'Select visible'}>
            <IconButton size="small" onClick={handleSelectAll} sx={{ p: '2px' }}>
              {allEntities.length > 0 && allEntities.every(([id]) => allSelected.includes(id))
                ? <DeselectIcon sx={{ fontSize: 14 }} />
                : <SelectAllIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          </Tooltip>
        )}

        <IconButton size="small" onClick={onToggle} sx={{ p: '2px', flexShrink: 0 }}>
          {open ? <ChevronRightIcon sx={{ fontSize: 15 }} /> : <ChevronLeftIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Box>

      {open && (
        <>
          {/* Search */}
          <Box sx={{ px: 1, py: '5px', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <TextField
              size="small" fullWidth
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 24, fontSize: 11, borderRadius: 1.5 },
                '& .MuiOutlinedInput-input': { py: 0, px: '4px' },
              }}
            />
          </Box>

          {hasSelection && (
            <Box sx={{ px: 1.5, py: '3px', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'primary.light' }}>
              <Typography sx={{ fontSize: 9, color: 'primary.dark', fontWeight: 700 }}>
                {allSelected.length} selected
              </Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto', py: '2px' }}>
            {allEntities.length === 0 && (
              <Typography sx={{ fontSize: 10, color: 'text.disabled', px: 1.5, py: 1 }}>
                No results
              </Typography>
            )}
            {Object.entries(entitiesByTech).map(([tech, entities], techIdx) => {
              const techFiltered = search.trim()
                ? entities.filter(([id]) => id.toLowerCase().includes(search.toLowerCase()))
                : entities
              if (techFiltered.length === 0) return null
              return (
                <Box key={tech}>
                  {comparing && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.5, py: '4px',
                      bgcolor: TECH_BADGE[tech]?.bg ?? '#f5f5f5',
                      borderTop: techIdx > 0 ? '2px solid' : 'none',
                      borderColor: TECH_BADGE[tech]?.border ?? '#e0e0e0',
                      flexShrink: 0,
                    }}>
                      <Box sx={{
                        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                        bgcolor: TECH_BADGE[tech]?.color ?? '#757575',
                      }} />
                      <Typography sx={{ fontSize: 9, fontWeight: 700, color: TECH_BADGE[tech]?.color ?? 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {tech}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ overflowY: 'auto', height: comparing ? 'calc((100vh - 280px) / 2)' : 'auto' }}>
                    {techFiltered.map(([id, idx]) => {
                      const color      = getSeriesColor(id, idx)
                      const isSelected = (selectedEntitiesMap[tech] ?? []).includes(id)
                      return (
                        <Box key={`${tech}-${id}`} onClick={() => handleClick(tech, id)} sx={{
                          display: 'flex', alignItems: 'center', gap: '7px',
                          px: 1.5, py: '3px', cursor: 'pointer',
                          bgcolor: isSelected ? 'primary.light' : 'transparent',
                          transition: 'background .15s',
                          '&:hover': { bgcolor: isSelected ? '#bbdefb' : 'action.hover' },
                        }}>
                          <Box sx={{ width: 18, height: 4, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
                          <Typography sx={{
                            fontSize: 10, fontFamily: 'IBM Plex Mono, monospace',
                            color: isSelected ? 'primary.dark' : 'text.secondary',
                            fontWeight: isSelected ? 700 : 400,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {id}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </>
      )}
    </Box>
  )
}
